import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import Court from '../../entities/court';
import Docket from '../../entities/docket';
import Matter from '../../entities/matter';
import CourtDeadlineRule from '../../entities/courtDeadlineRule';

/*
 * Delete/nullify anywhere this court is used in other entities
 *
 * @param {Court} court
 */
export function deleteCourt(court: Court, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = court.id;
    }
    /* Simple delete usages */
        
    store.getState().dockets
        .getMappingFor('courtId', court.id)
        .filter(docketTemp => docketTemp.status !== 'D')
        .forEach(docketTemp => {
            const _docket = docketTemp.clone();
            _docket.status =  'U';
            _docket.addedOn = newHRTimestamp();
            _docket.addedBy = getCurrentUserId();
            _docket.importSource = targetImportSource;
            _docket.importId = mergeId;

            _docket.courtId = null;
            store.dispatch(Actions.DocketActions.saveDocketQueue(_docket));
        });

    store.getState().matters
        .getMappingFor('courtId', court.id)
        .filter(matterTemp => matterTemp.status !== 'D')
        .forEach(matterTemp => {
            const _matter = matterTemp.clone();
            _matter.status =  'U';
            _matter.addedOn = newHRTimestamp();
            _matter.importSource = targetImportSource;
            _matter.importId = mergeId;

            _matter.courtIds = _matter.courtIds?.filter(id => id !== court.id) || [];
            store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
        });

    store.getState().courts
        .getMappingFor('courtId', court.id)
        .filter(courtTemp => courtTemp.status !== 'D')
        .forEach(courtTemp => {
            const _court = courtTemp.clone();
            _court.status =  'U';
            _court.addedOn = newHRTimestamp();
            _court.addedBy = getCurrentUserId();
            _court.importSource = targetImportSource;
            _court.importId = mergeId;

            _court.parentCourtId = null;
            store.dispatch(Actions.CourtActions.saveCourtQueue(_court));
        });

    store.getState().courtDeadlineRules
        .getMappingFor('courtId', court.id)
        .filter(courtDeadlineRuleTemp => courtDeadlineRuleTemp.status !== 'D')
        .forEach(courtDeadlineRuleTemp => {
            const _courtDeadlineRule = courtDeadlineRuleTemp.clone();
            _courtDeadlineRule.status =  'D';
            _courtDeadlineRule.addedOn = newHRTimestamp();
            _courtDeadlineRule.addedBy = getCurrentUserId();
            _courtDeadlineRule.importSource = targetImportSource;
            _courtDeadlineRule.importId = mergeId;

            store.dispatch(Actions.CourtDeadlineRuleActions.saveCourtDeadlineRuleQueue(_courtDeadlineRule));
        });

    /* Remove the actual court */
    court.status = 'D';
    court.addedOn = newHRTimestamp();
    court.addedBy = getCurrentUserId();
    court.importSource = targetImportSource;
    court.importId = mergeId;
    store.dispatch(Actions.CourtActions.removeCourtQueue(court));
    
}

/*
 * Replace anywhere this court is used in other entities.
 *
 * @param {Court} court
 * @param {Court} replaceWith
 * @return {Court} - Court will have refs stripped away
 */
export function replaceCourt(court: Court, replaceWith: Court, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = court.id;
    }
    /* Simple replace usages */
        
    store.getState().dockets
        .getMappingFor('courtId', court.id)
        .filter(docketTemp => docketTemp.status !== 'D')
        .forEach(docketTemp => {
            const _docket = docketTemp.clone();
            _docket.status =  'U';
            _docket.addedOn = newHRTimestamp();
            _docket.addedBy = getCurrentUserId();
            _docket.importSource = targetImportSource;
            _docket.importId = mergeId;

            _docket.courtId = replaceWith.id;
            store.dispatch(Actions.DocketActions.saveDocketQueue(_docket));
        });

    store.getState().matters
        .getMappingFor('courtId', court.id)
        .filter(matterTemp => matterTemp.status !== 'D')
        .forEach(matterTemp => {
            const _matter = matterTemp.clone();
            _matter.status =  'U';
            _matter.addedOn = newHRTimestamp();
            _matter.importSource = targetImportSource;
            _matter.importId = mergeId;

            _matter.courtIds = _matter.courtIds?.map(id => (id === court.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
        });

    store.getState().courts
        .getMappingFor('courtId', court.id)
        .filter(courtTemp => courtTemp.status !== 'D')
        .forEach(courtTemp => {
            const _court = courtTemp.clone();
            _court.status =  'U';
            _court.addedOn = newHRTimestamp();
            _court.addedBy = getCurrentUserId();
            _court.importSource = targetImportSource;
            _court.importId = mergeId;

            _court.parentCourtId = replaceWith.id;
            store.dispatch(Actions.CourtActions.saveCourtQueue(_court));
        });

    store.getState().courtDeadlineRules
        .getMappingFor('courtId', court.id)
        .filter(courtDeadlineRuleTemp => courtDeadlineRuleTemp.status !== 'D')
        .forEach(courtDeadlineRuleTemp => {
            const _courtDeadlineRule = courtDeadlineRuleTemp.clone();
            _courtDeadlineRule.status =  'U';
            _courtDeadlineRule.addedOn = newHRTimestamp();
            _courtDeadlineRule.addedBy = getCurrentUserId();
            _courtDeadlineRule.importSource = targetImportSource;
            _courtDeadlineRule.importId = mergeId;

            _courtDeadlineRule.courtId = replaceWith.id;
            store.dispatch(Actions.CourtDeadlineRuleActions.saveCourtDeadlineRuleQueue(_courtDeadlineRule));
        });

    return court;
}

/*
 * Determine the impact of deleting this court from other entities
 *
 * @param {Court} court
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactCourt(court: Court) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const docketCourtIdImpact = state.dockets
          .getMappingFor('courtId', court.id)
          .filter(docket => docket.status !== 'D');

    docketCourtIdImpact.forEach(() => impacts.push('dockets'));

    const matterCourtIdsImpact = state.matters
          .getMappingFor('courtId', court.id)
          .filter(matter => matter.status !== 'D');

    matterCourtIdsImpact.forEach(() => impacts.push('matters'));

    const courtParentCourtIdImpact = state.courts
          .getMappingFor('courtId', court.id)
          .filter(court => court.status !== 'D');

    courtParentCourtIdImpact.forEach(() => impacts.push('courts'));

    const courtDeadlineRuleCourtIdImpact = state.courtDeadlineRules
          .getMappingFor('courtId', court.id)
          .filter(courtDeadlineRule => courtDeadlineRule.status !== 'D');

    courtDeadlineRuleCourtIdImpact.forEach(() => impacts.push('courtDeadlineRules'));

    return util.countDistinct(impacts);
}